import React, { useState, useRef, useEffect } from "react";

interface PopoverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  className?: string;
  leftonly?: boolean;
}

export const PopoverTrigger: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className }) => (
  <div className={"inline-flex"}>{children}</div>
);

export const PopoverContent: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className }) => (
  <div
    className={`z-50 w-72 rounded-md border bg-white p-4 text-black shadow-md ${className}`}
  >
    {children}
  </div>
);

export const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  className,
  leftonly = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popoverRef.current &&
      !popoverRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Clone the trigger child and add onClick handler
  const triggerElement = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === PopoverTrigger) {
      const childProps: any = child.props.children;

      if (React.isValidElement(childProps)) {
        return React.cloneElement(childProps, {
          ...(childProps.props as any),
          onClick: (e: React.MouseEvent) => {
            handleClick();
          },
        });
      }

      return React.cloneElement(child as React.ReactElement, {
        onClick: handleClick,
      });
    }
    return child;
  });

  // Handle content rendering
  const contentElement = React.Children.map(content, (child) => {
    if (React.isValidElement(child) && child.type === PopoverContent) {
      return child;
    }
    return <PopoverContent>{child}</PopoverContent>;
  });

  return (
    <div className={"relative inline-block"} ref={popoverRef}>
      {triggerElement}
      {isOpen && (
        <div className={`${leftonly ? "" : "right-0"} absolute z-50 mt-2`}>
          {contentElement}
        </div>
      )}
    </div>
  );
};
